/* eslint-disable camelcase */
import { IndicatorName, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { IndicatorProductSubscriptionConfiguration } from '@/api/rest/resources/types/IndicatorProduct';
import { WidgetSlot } from '@/api/rest/resources/types/widget';
import { useControlsContext } from '@/components/widgets/o/Map/hooks/useFormControls';
import { useIsPlotPage } from '@/hooks/useIsPlotPage';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { usePlotsForOrganization } from '@/pages/shared/hooks/usePlotsForOrganization';

import { MediaMapLayerDataItem } from './constants';
import { MediaMapLayer } from './MediaMapLayer';

export const MediaMapLayerRenderer = () => {
  const controlsForm = useControlsContext();
  const [configurations] = controlsForm.watch(['inSituLayerConfiguration']);

  if (!Array.isArray(configurations)) {
    return null;
  }

  return (
    <>
      {configurations.map((c) => (
        <div key={c.id}>
          <MediaMapLayerRendererBase configuration={c} />
        </div>
      ))}
    </>
  );
};

const MediaMapLayerRendererBase = ({ configuration }: { configuration: IndicatorProductSubscriptionConfiguration }) => {
  const controlsForm = useControlsContext();
  const [selectedYear] = controlsForm.watch(['year']);

  const isPlotPage = useIsPlotPage();

  const widget = configuration?.widget?.widget_format?.[0] as WidgetSlot;

  if (!configuration || !widget) {
    return null;
  }

  if (isPlotPage) {
    return (
      <div key={configuration.id}>
        <MediaMapPlotRenderer configuration={configuration} selectedYear={selectedYear} />
      </div>
    );
  }

  return (
    <div key={configuration.id}>
      <MediaMapOrgRenderer configuration={configuration} selectedYear={selectedYear} />
    </div>
  );
};

const MediaMapPlotRenderer = ({
  configuration,
  selectedYear,
}: {
  configuration: IndicatorProductSubscriptionConfiguration;
  selectedYear: string;
}) => {
  const widget = (configuration?.widget?.widget_format ?? []) as WidgetSlot[];

  if (!widget) {
    throw new Error(`No widget format found for configuration id ${configuration.id}`);
  }

  const factNames = widget.map((w) => w?.relatedData?.factName ?? IndicatorName.unknown);
  const dateFiltersAll = widget.map((w) => w?.relatedData?.dateFilters ?? {});
  const dateFilters = {
    end_date_inclusive: dateFiltersAll
      .map((df) => df.end_date_inclusive)
      .sort((a, b) => b - a)
      .at(0),
    start_date_inclusive: dateFiltersAll
      .map((df) => df.start_date_inclusive)
      .sort((a, b) => a - b)
      .at(0),
  };

  const sourceRef = configuration?.file_info?.[0]?.id;

  const facts = usePlotFacts<string>(factNames, undefined, undefined, undefined, sourceRef, dateFilters);
  const data = getLayerDataFromFacts(facts, selectedYear);

  return <MediaMapLayer data={data} configurationId={configuration.id} />;
};

const MediaMapOrgRenderer = ({
  configuration,
  selectedYear,
}: {
  configuration: IndicatorProductSubscriptionConfiguration;
  selectedYear: string;
}) => {
  const widget = configuration?.widget?.widget_format ?? [];

  if (!widget) {
    throw new Error(`No widget format found for configuration ${configuration.id}`);
  }

  if (Array.isArray(widget) && widget.length > 1) {
    throw new Error(
      `Widget format array has more than one element for configuration ${configuration.id}. Only 1 element is allowed`,
    );
  }

  const factName = widget.at(0).relatedData?.factName ?? IndicatorName.unknown;
  const { end_date_inclusive = undefined, start_date_inclusive = undefined } =
    widget.at(0).relatedData?.dateFilters ?? {};
  const sourceRef = configuration?.file_info?.[0]?.id;

  // We query usePlotsForOrganization and not the organization APIs because we need the facts for each plot, not aggregated
  const plotsWithFacts =
    usePlotsForOrganization(undefined, {
      fact_names: factName,
      ...(end_date_inclusive ? { end_date_inclusive } : {}),
      ...(start_date_inclusive ? { start_date_inclusive } : {}),
      source_ref: sourceRef,
    })?.data?.results ?? [];

  const facts = (plotsWithFacts.flatMap((plot) => plot.facts ?? []) ?? []) as PlotReportFactElement<string | null>[];
  const data = getLayerDataFromFacts(facts, selectedYear);

  return <MediaMapLayer data={data} configurationId={configuration.id} />;
};

const getLayerDataFromFacts = (
  facts: PlotReportFactElement<string | null>[] | undefined,
  selectedYear: string,
): MediaMapLayerDataItem[] => {
  return (
    facts?.flatMap((fact) => {
      if (!fact || !fact.value || !fact.date) {
        return [];
      }

      if (!fact.date.includes(selectedYear)) {
        return [];
      }

      const labels = fact.labels ?? [];
      const longitudeLabel = labels.find((label) => label.name === 'longitude');
      const latitudeLabel = labels.find((label) => label.name === 'latitude');

      if (!longitudeLabel?.value || !latitudeLabel?.value) {
        return [];
      }

      const otherLabels = labels.filter((label) => label.name !== 'longitude' && label.name !== 'latitude');

      return [
        {
          longitude: longitudeLabel.value as string,
          latitude: latitudeLabel.value as string,
          value: fact.value,
          ...Object.fromEntries(otherLabels.map((label) => [label.name, label.value])),
        },
      ];
    }) ?? []
  );
};
